import { Feature } from '.generated/templates/Feature.Items.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { getThemeClasses, Theme } from 'lib/get-theme';
import { getEnum } from 'lib/get-enum';
import classNames from 'classnames';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import VideoPlayer from '../../../helpers/VideoPlayer/VideoPlayer';
import { useEffect, useState } from 'react';
import FullWidthVideoPromoOverlay from './FullWidthVideoPromoOverlay';
import useWindowDimensions from 'components/helpers/WindowsSize/WindowSize';

// Define the props for the FullWidthVideoPromo component
export type FullWidthVideoPromoProps = Sitecore.Override.ComponentBase &
  Feature.Components.Promo.Fields.FullWidthVideoPromo;

const FullWidthVideoPromo = ({ fields, rendering }: FullWidthVideoPromoProps): JSX.Element => {
  // If fields are missing, display a placeholder component
  if (!fields) {
    return <MissingDataSource />;
  }
  const [screenWidth, setScreenWidth] = useState(1440);
  const { width } = useWindowDimensions();
  useEffect(() => {
    setScreenWidth(width);
  }, [width]);

  // Get the theme value from the fields
  const themeValue = getEnum<Theme>(fields?.backgroundStyle);
  // Get the unique ID for the component
  const componentAnchorId = rendering?.uid;

  const showOverlay = fields?.showOverlay?.value;

  return (
    <div
      className={classNames(
        'w-full',
        'relative',
        'flex',
        'flex-col',
        getThemeClasses(themeValue),
        'bg-theme-bg',
        'text-theme-text',
        'anchorIdIndentifier'
      )}
      data-current-theme={getThemeClasses(themeValue)}
      data-component="authorable/promo/fullwidthvideopromo"
      id={componentAnchorId}
    >
      <div className={classNames('w-full', 'relative')}>
        <VideoPlayer
          desktopVideoUrl={fields?.desktopVideoUrl?.value}
          mobileVideoUrl={fields?.mobileVideoUrl?.value}
          stopImageDesktop={fields?.desktopThumbnailImage}
          stopImageMobile={fields?.mobileThumbnailImage}
          loopCount={fields?.loopCount?.value}
          infiniteLoop={fields?.infiniteLoop?.value}
          screenWidth={screenWidth}
        />
        <div className="mx-auto w-full container-content-width">
          <div className="container-content-padding-x md:space-y-12 space-y-8">
            {showOverlay && (
              <div
                className={classNames(
                  'absolute',
                  'top-1/2',
                  'z-10',
                  'hidden',
                  'max-w-md',
                  '-translate-y-1/2',
                  'rounded-xl',
                  'p-8',
                  'shadow-lg',
                  'md:flex',
                  'md:flex-col',
                  'bg-theme-bg-opacity/80'
                )}
              >
                <FullWidthVideoPromoOverlay fields={fields} rendering={rendering} />
              </div>
            )}
          </div>
        </div>
      </div>
      {showOverlay && (
        <div className={classNames('md:hidden', 'flex', 'flex-col', 'px-6', 'py-8', 'text-left')}>
          <FullWidthVideoPromoOverlay fields={fields} rendering={rendering} />
        </div>
      )}
    </div>
  );
};

export default FullWidthVideoPromo;
